import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { generateRandomPrice, calculateProfitLoss, calculateProfitRate, generateRandomLog } from '../utils/simulation';
import positionsData from '../data/positions.json';
import accountData from '../data/accountInfo.json';
import logsData from '../data/systemLogs.json';

const SimulationContext = createContext();

export const useSimulation = () => {
  const context = useContext(SimulationContext);
  if (!context) {
    throw new Error('useSimulation must be used within SimulationProvider');
  }
  return context;
};

export const SimulationProvider = ({ children }) => {
  const [positions, setPositions] = useState(positionsData);
  const [account, setAccount] = useState(accountData);
  const [logs, setLogs] = useState(logsData);
  const [flashingCells, setFlashingCells] = useState({});

  const updatePositions = useCallback(() => {
    setPositions((prev) =>
      prev.map((pos) => {
        const newPrice = generateRandomPrice(pos.currentPrice);
        const profitLoss = calculateProfitLoss(pos.avgPrice, newPrice, pos.quantity);
        const profitRate = calculateProfitRate(pos.avgPrice, newPrice);

        setFlashingCells((prev) => ({ ...prev, [pos.id]: true }));
        setTimeout(() => {
          setFlashingCells((prev) => ({ ...prev, [pos.id]: false }));
        }, 500);

        return {
          ...pos,
          currentPrice: newPrice,
          profitLoss,
          profitRate,
        };
      })
    );
  }, []);

  const updateAccount = useCallback(() => {
    setPositions((currentPositions) => {
      const totalProfitLoss = currentPositions.reduce((sum, pos) => sum + pos.profitLoss, 0);
      const totalAssets = accountData.deposit + totalProfitLoss;
      const totalProfitRate = (totalProfitLoss / accountData.deposit) * 100;

      setAccount((prev) => ({
        ...prev,
        totalAssets,
        totalProfitLoss,
        totalProfitRate: +totalProfitRate.toFixed(2),
      }));

      return currentPositions;
    });
  }, []);

  const addLog = useCallback(() => {
    const newLog = generateRandomLog();
    setLogs((prev) => {
      const updated = [newLog, ...prev];
      return updated.slice(0, 200); // 최대 200개 유지
    });
  }, []);

  useEffect(() => {
    const priceInterval = setInterval(() => {
      updatePositions();
      updateAccount();
    }, 4000);

    const logInterval = setInterval(() => {
      addLog();
    }, 7000);

    return () => {
      clearInterval(priceInterval);
      clearInterval(logInterval);
    };
  }, [updatePositions, updateAccount, addLog]);

  return (
    <SimulationContext.Provider value={{ positions, account, logs, flashingCells }}>
      {children}
    </SimulationContext.Provider>
  );
};
